import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Alert,
  Paper,
  Link,
} from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { Link as RouterLink } from 'react-router-dom';
import { appointmentService } from '../../services/appointment.service';
import { AppointmentResponseDto, AppointmentStatus } from '../../types/appointment.types';
import { UserAvatar } from '../common/UserAvatar';
import { AppointmentCountdown } from '../appointments/AppointmentCountdown';

const STATUS_CONFIG: Record<AppointmentStatus, { label: string; color: 'warning' | 'success' | 'error' | 'default' }> = {
  [AppointmentStatus.PENDING]:   { label: 'Очікує підтвердження', color: 'warning' },
  [AppointmentStatus.CONFIRMED]: { label: 'Підтверджено',          color: 'success' },
  [AppointmentStatus.CANCELLED]: { label: 'Скасовано',             color: 'error'   },
  [AppointmentStatus.COMPLETED]: { label: 'Завершено',             color: 'default' },
};

const ACTIVE_STATUSES = new Set([AppointmentStatus.PENDING, AppointmentStatus.CONFIRMED]);

const ExpertAppointmentsWidget: React.FC = () => {
  const [appointments, setAppointments] = useState<AppointmentResponseDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await appointmentService.getExpertAppointments({ page: 1, limit: 20 });
      const active = response.data
        .filter(a => ACTIVE_STATUSES.has(a.status))
        .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime());
      setAppointments(active);
    } catch (err: any) {
      const msg = err.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(', ') : msg || 'Не вдалося завантажити записи.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Актуальні зустрічі</Typography>
        <Link component={RouterLink} to="/expert/appointments" variant="caption" underline="hover">
          Всі зустрічі
        </Link>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && appointments.length === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, color: 'text.secondary' }}>
          <EventNoteIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
          <Typography variant="body2">Немає активних зустрічей</Typography>
        </Box>
      )}

      {!loading && !error && appointments.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {appointments.map(a => {
            const status = STATUS_CONFIG[a.status];
            return (
              <Paper key={a.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <UserAvatar path={a.client.avatar ?? null} name={a.client.name} sx={{ width: 40, height: 40 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {a.client.name}
                      </Typography>
                      <Chip label={status.label} color={status.color} size="small" variant="outlined" />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(a.scheduledAt).toLocaleString('uk-UA', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                      {' · '}{a.durationMinutes} хв
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <AppointmentCountdown scheduledAt={a.scheduledAt} status={a.status} />
                    </Box>
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ExpertAppointmentsWidget;
